import { memo, useCallback, useEffect, useState } from 'react'
import type { Divisao, Subitem } from '../api'
import { faixaDe, proporcao } from '../faixas'
import { larguraBarra, milhoes, valorCurto } from '../formato'
import { usarNumeroAnimado } from '../ganchos/usarNumeroAnimado'
import {
  SEM_MEDICAO,
  classeDoCard,
  classeDoVelocimetro,
  foiMedido,
  mesmaDivisao,
  temSubitens,
  vendidoParaAgulha,
} from '../cartao'
import { Velocimetro } from './Velocimetro'

/**
 * O card de uma divisão — porte direto da `montar()` do protótipo.
 *
 * A leitura a 4 metros manda no arranjo: a **meta fica embaixo do nome** e o
 * **vendido embaixo do velocímetro**. Não existe o texto "de R$ X", e nenhum
 * número vai à tela em percentual — a proporção só posiciona a agulha e escolhe
 * a cor da faixa.
 *
 * `vendido: null` mostra `—` com a agulha no início da escala: a divisão não foi
 * medida, o que é diferente de ter vendido zero.
 */

type Props = {
  divisao: Divisao
  aoClicar: (id: string) => void
  /** Entrou pedido nesta divisão: liga a classe `.mudou` por 1 s. */
  piscar: boolean
}

/** Uma linha de subitem: nome, valor, barra e a meta dele em R$ abaixo. */
function LinhaSubitem({ subitem }: { subitem: Subitem }) {
  const parte = proporcao(vendidoParaAgulha(subitem.vendido), subitem.meta)

  return (
    <div className="sub">
      <span className="sub-nome">
        {subitem.rotulo}
        {subitem.obs ? <span className="sub-obs"> ({subitem.obs})</span> : null}
      </span>
      <span className="sub-valor">
        {foiMedido(subitem.vendido) ? valorCurto(subitem.vendido) : SEM_MEDICAO}
      </span>
      <div className="trilha">
        {/* Largura e cor são DADO, não layout: a barra usa a mesma escala do
            velocímetro (teto em 120 % da meta), para barra cheia e agulha no fim
            significarem a mesma coisa. */}
        <div
          className="preenche"
          style={{ width: larguraBarra(parte), background: faixaDe(parte).hex }}
        />
      </div>
      <span className="sub-meta">meta {milhoes(subitem.meta)}</span>
    </div>
  )
}

function CardMarcaBase({ divisao, aoClicar, piscar }: Props) {
  /* O vendido SOBE até o valor novo, com a duração crescendo com o salto
     (`usarNumeroAnimado`). Chamado aqui, no corpo do componente da marca:
     cada card anima o seu, e um card parado não paga nada por isso. */
  const vendidoSubindo = usarNumeroAnimado(divisao.vendido)
  /* O pulso é o `key` do nó do valor, e não só um booleano de classe: acrescentar
     `.mudou` a um nó que já a tem NÃO reinicia a animação, e dois pedidos em
     sequência na mesma divisão passariam sem realce. Trocar o `key` remonta
     aquele único `<div>`, que é o que faz a animação rodar de novo. */
  const [pulso, setPulso] = useState(0)

  useEffect(() => {
    if (!piscar) return
    setPulso(numero => numero + 1)
    /* Limpar na desmontagem: o painel fica 4 dias ligado e temporizador solto
       em componente que saiu da tela é vazamento acumulado. */
    const temporizador = setTimeout(() => setPulso(0), 1000)
    return () => clearTimeout(temporizador)
  }, [piscar, divisao.vendido])

  /* Estável entre repinturas de valor: handler novo a cada pedido jogaria fora
     o `memo` de quem recebe esta função. */
  const abrir = useCallback(() => aoClicar(divisao.id), [aoClicar, divisao.id])

  const parte = proporcao(vendidoParaAgulha(divisao.vendido), divisao.meta)

  return (
    /* `<button>` em vez de `<article role="button">`: o teclado e o foco vêm de
       graça, e o CSS já neutraliza a aparência (`.card` reseta `font`, `color` e
       `text-align`). */
    <button
      type="button"
      className={classeDoCard(divisao)}
      data-marca={divisao.id}
      onClick={abrir}
    >
      <div className="card-topo">
        <div className="logo">
          {/* `alt` é a SIGLA, não vazio: logo apagado do disco depois de
              cadastrado devolve 404, e com `alt=""` o quadro fica em branco —
              o card perde a identidade justamente no telão. Com a sigla, o
              navegador desenha o texto no lugar da imagem que falhou. */}
          {divisao.logo ? <img src={divisao.logo} alt={divisao.sigla} /> : divisao.sigla}
        </div>
        <div className="card-id">
          <div className="nome-marca">{divisao.nome}</div>
          {/* Embaixo do nome vem a META. O vendido fica sob o velocímetro. */}
          <div className="meta-marca">{milhoes(divisao.meta)}</div>
        </div>
        <div className="bloco-velocimetro">
          {/* Só primitivos: o `memo` do velocímetro depende disso para não
              redesenhar o SVG a cada valor — só a agulha se move. */}
          <Velocimetro proporcao={parte} classe={classeDoVelocimetro(divisao)} />
          <div key={pulso} className={pulso > 0 ? 'vendido-marca mudou' : 'vendido-marca'}>
            {/* O valor SOBE até o número novo (13/10). O `foiMedido` olha o
                valor REAL, não o animado: é ele que diz se alguém mediu. */}
            {foiMedido(divisao.vendido) ? valorCurto(vendidoSubindo ?? divisao.vendido) : SEM_MEDICAO}
          </div>
        </div>
      </div>

      {/* O número de subitens vem do cadastro: vazio, 1, 3 ou 4. */}
      {temSubitens(divisao) ? (
        <div className="subitens">
          {divisao.subitens.map(subitem => (
            <LinhaSubitem key={subitem.codigo} subitem={subitem} />
          ))}
        </div>
      ) : null}
    </button>
  )
}

/* A comparação é pelos valores que de fato mudam (ver `mesmaDivisao`). Sem ela,
   cada pedido que entra reconcilia os 8 cards e os 8 velocímetros — por 4 dias
   seguidos. */
export const CardMarca = memo(
  CardMarcaBase,
  (anterior, novo) =>
    anterior.piscar === novo.piscar &&
    anterior.aoClicar === novo.aoClicar &&
    mesmaDivisao(anterior.divisao, novo.divisao),
)
